import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ActionBar, DataGrid, type DesktopAction, type GridColumn } from '@/components/desktop';
import { AppIcon } from '@/components/AppIcon';
import { SavedWorkDetail } from '@/components/SavedWorkDetail';
import { ErrorNote, OkNote, StatusPill, errorMessage } from '@/components/ui';
import { formatDateTime } from '@/format';
import { clearSentOfflineWork } from '@/offline/engine';
import { canFix, fixPiece, pieceStatus, sendPieceAgain, throwPieceAway } from '@/offline/pieceActions';
import { piecesOf, type Piece } from '@/offline/pieces';
import { useOutboxStatus } from '@/offline/sender';
import { useSavedWork } from '@/offline/useSavedWork';
import { screenLabel } from '@/shell/modules';
import { onWindowMessage } from '@/stores/channel';
import { confirmAction } from '@/stores/confirm';

/**
 * Offline work (.docs/023): everything saved on this PC with no answer from
 * the server, where it has got to, and what the server refused.
 *
 * Saved offline, work goes by itself once the line is back, and nobody has to
 * do anything — but people want to see that it is there, and later that it
 * arrived. So it is kept here after it has gone, with the number the server
 * gave it, until somebody clears it.
 *
 * Refusals are here too, whether saved offline or not: the server has looked
 * at the work and said no, and nothing after it in the same lane can go until
 * somebody decides.
 */

type Tab = 'waiting' | 'refused' | 'sent';

const TABS: ReadonlyArray<{ id: Tab; label: string; intro: string; empty: string }> = [
  {
    id: 'waiting',
    label: 'Waiting to send',
    intro: 'Saved on this PC. Each goes to the server by itself as soon as the connection is back — there is nothing more to do.',
    empty: 'Nothing is waiting. Everything saved on this PC has reached the server.',
  },
  {
    id: 'refused',
    label: 'Needs attention',
    intro: 'The server looked at these and said no. Open one to see why, then fix it, send it again or throw it away.',
    empty: 'Nothing is stuck. The server has taken everything it was sent.',
  },
  {
    id: 'sent',
    label: 'Sent',
    intro: 'Work saved without a connection that has since reached the server. It stays here until you clear it.',
    empty: 'Nothing saved offline has been sent yet.',
  },
];

export function NeedsAttention() {
  const [chosenTab, setChosenTab] = useState<Tab | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const status = useOutboxStatus();
  const queryClient = useQueryClient();

  // Read again as work is sent, refused or saved in another tab.
  const saved = useSavedWork();
  const all = saved.data ?? [];
  useEffect(
    () =>
      onWindowMessage((m) => {
        if (m.type === 'outbox') void queryClient.invalidateQueries({ queryKey: ['outbox'] });
      }),
    [queryClient],
  );

  // Newest first: what was just saved is what is looked for.
  const pieces = piecesOf(all).reverse();
  const byTab: Record<Tab, Piece[]> = {
    waiting: pieces.filter((p) => p.state === 'waiting'),
    refused: pieces.filter((p) => p.state === 'refused'),
    sent: pieces.filter((p) => p.state === 'sent' && p.keptOffline),
  };
  // Until somebody picks one: what needs them, else what waits, else what went.
  const tab = chosenTab ?? (byTab.refused.length ? 'refused' : byTab.waiting.length || !byTab.sent.length ? 'waiting' : 'sent');
  const shown = TABS.find((t) => t.id === tab)!;

  function said(message: string) {
    setOpenId(null);
    setDone(message);
  }

  async function clearSent() {
    const ok = await confirmAction({
      title: 'Clear the sent list?',
      message: `The ${byTab.sent.length} ${byTab.sent.length === 1 ? 'piece' : 'pieces'} of work in Sent will be taken off this list. They are already with the server, and stay on their own screens.`,
      confirmLabel: 'Clear sent',
      cancelLabel: 'Keep them',
    });
    if (!ok) return;
    await clearSentOfflineWork();
    setDone('The sent list is cleared.');
  }

  const open = openId ? pieces.find((p) => p.id === openId) : undefined;
  if (open) {
    return (
      <SavedWorkDetail
        piece={open}
        all={all}
        backLabel="Back to Offline work"
        onBack={() => setOpenId(null)}
        onDone={said}
      />
    );
  }

  const columns: Array<GridColumn<Piece>> = [
    { key: 'what', header: 'What', width: 340, render: (p) => <span title={p.lead.describes}>{p.lead.describes}</span> },
    { key: 'where', header: 'Typed on', width: 150, render: (p) => <span className="dim">{screenLabel(p.lead.screen)}</span> },
    { key: 'saved', header: 'Saved on this PC', width: 170, render: (p) => formatDateTime(new Date(p.createdAt)) },
    ...(tab === 'sent'
      ? [
          { key: 'sent', header: 'Sent', width: 170, render: (p: Piece) => (p.sentAt ? formatDateTime(new Date(p.sentAt)) : '—') },
          { key: 'doc', header: 'Document', width: 130, render: (p: Piece) => <span className="mono">{p.documentNo ?? '—'}</span> },
        ]
      : tab === 'refused'
        ? [{ key: 'why', header: 'Why', width: 320, render: (p: Piece) => <span className="stuck__why" title={p.refused?.lastError ?? undefined}>{p.refused?.lastError ?? 'The server refused it.'}</span> }]
        : []),
    { key: 'status', header: 'Status', width: 130, render: (p) => <StatusPill status={pieceStatus(p)} /> },
  ];

  function rowActions(piece: Piece): DesktopAction[] {
    return [
      { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => setOpenId(piece.id), allowed: true },
      ...(piece.state === 'refused'
        ? [
            { label: 'Fix it', icon: 'edit' as const, onSelect: () => fixPiece(piece), allowed: canFix(piece), separatorBefore: true },
            { label: 'Send it again', icon: 'repeat' as const, onSelect: () => void sendPieceAgain(piece).then(() => setDone(`${piece.lead.describes}: sent again.`)), allowed: true },
            {
              label: 'Throw it away',
              icon: 'remove' as const,
              onSelect: () => void throwPieceAway(piece).then((gone) => gone && setDone(`${piece.lead.describes}: thrown away.`)),
              allowed: true,
            },
          ]
        : []),
    ];
  }

  const toolbarActions: DesktopAction[] =
    tab === 'sent' ? [{ label: 'Clear sent', icon: 'remove', onSelect: () => void clearSent(), allowed: true, enabled: byTab.sent.length > 0, reason: 'Nothing to clear' }] : [];

  return (
    <div className="workbench">
      <div className="workbench__main offlinework">
        <div className="offlinework__bar">
          <div className="viewtoggle" role="tablist" aria-label="Offline work">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`viewtoggle__btn${tab === t.id ? ' viewtoggle__btn--on' : ''}`}
                onClick={() => {
                  setChosenTab(t.id);
                  setSelectedKey(null);
                  setDone(null);
                }}
              >
                {t.label}
                <span className={`offlinework__count${t.id === 'refused' && byTab.refused.length ? ' offlinework__count--alert' : ''}`}>
                  {byTab[t.id].length}
                </span>
              </button>
            ))}
          </div>
          {toolbarActions.length > 0 && <ActionBar actions={toolbarActions} />}
        </div>

        {status.held === 'too-old' && (
          <ErrorNote message="This version of Poultry MS is too old to send work. Reload the page to get the new version. Nothing saved on this PC is lost." />
        )}
        {status.held === 'sign-in' && (
          <ErrorNote message="Your sign-in has ended. Sign in again with a connection to send this work." />
        )}
        {saved.error && <ErrorNote message={errorMessage(saved.error, 'Could not read the work saved on this PC.')} />}
        {done && <OkNote message={done} />}

        <p className="muted offlinework__intro">
          <AppIcon kind={tab === 'refused' ? 'statusRefused' : tab === 'sent' ? 'statusDone' : 'statusPending'} size={14} /> {shown.intro}
        </p>

        <DataGrid
          id={`offline-work-${tab}`}
          label={shown.label}
          columns={columns}
          rows={byTab[tab]}
          rowKey={(p) => p.id}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onOpen={(p) => setOpenId(p.id)}
          openOnClick
          actions={rowActions}
          loading={saved.isPending}
          empty={shown.empty}
        />
      </div>
    </div>
  );
}
